import { Howl, Howler } from 'howler';
import { BehaviorSubject, interval, Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

export class Sound {
  private readonly onEndSubject = new BehaviorSubject<void>(null);
  private readonly onStopSubject = new BehaviorSubject<void>(null);
  private readonly onPlaySubject = new BehaviorSubject<void>(null);
  private readonly onPauseSubject = new BehaviorSubject<void>(null);
  private readonly onDurationSubject = new BehaviorSubject<number>(0);
  private readonly onMaxDurationSubject = new BehaviorSubject<number>(0);

  private readonly playingSubject = new BehaviorSubject<boolean>(false);

  private updateObservable$: Observable<number>;
  private updateSubscriber: Subscription;

  private _isPlaying: boolean;

  private isLoaded = false;

  private shouldSeekPrev = true;
  private shouldSeekNext = true;

  private lastSeek = 0;

  private sound: Howl;

  private readonly previousVolume: number;

  constructor(
    private readonly audioUrl: string,
    private _startSegment: number,
    private _endSegment: number,
    private _introPoint: number,
    private _isIntroPointActive: boolean,
    private _outroPoint: number,
    private _isOutroPointActive: boolean,
    private _mixPoint: number,
    private _isMixPointActive: boolean,
    // @ts-ignore
    private readonly _type: string,
    private _prevSound?: Sound,
    private _nextSound?: Sound,
  ) {
    this._isPlaying = false;
    this.setupTrack();
    this.previousVolume = Howler.volume();
    Howler.volume(1);
  }

  destroy() {
    this.sound.stop();
    this.sound.unload();
    this.unsubscribe();
    if (this._nextSound) {
      this._nextSound.destroy();
    }
    if (this._prevSound) {
      this._prevSound.destroy();
    }
    Howler.volume(this.previousVolume);
  }

  private setupTrack() {
    if (this.sound) {
      this.sound.stop();
      this.sound.unload();
    }
    this.sound = new Howl({
      src: [this.audioUrl],
      html5: true,
    });
    this.setupListeners();
    this.sound.volume(0);
    this.sound.play();
    this.sound.pause();
    this.sound.volume(1);
  }

  private setupListeners() {
    const onMusicStop = (endNextInstant = true) => {
      this.unsubscribe();
      this.shouldSeekPrev = true;
      this.shouldSeekNext = true;
      if (this._prevSound) {
        this._prevSound.stopAudio();
      }
      if (this._nextSound) {
        setTimeout(
          () => {
            this._nextSound?.stopAudio();
          },
          endNextInstant ? 0 : 3000,
        );
      }
      this._isPlaying = false;
      this.playingSubject.next(this.isPlaying);
    };

    this.sound.once('load', () => {
      this.shouldSeekPrev = true;
      this.shouldSeekNext = true;
      this.onMaxDurationSubject.next(this.getMaxDuration());
      this.isLoaded = true;
    });

    this.sound.on('end', () => {
      this.onEndSubject.next(null);
      onMusicStop(false);
    });
    this.sound.on('stop', () => {
      this.onStopSubject.next(null);
      onMusicStop();
    });
    this.sound.on('pause', () => {
      this.onPauseSubject.next(null);
      onMusicStop();
    });
    this.sound.on('play', () => {
      this._isPlaying = true;
      this.onPlaySubject.next(null);
      this.playingSubject.next(this.isPlaying);

      if (this.sound.playing()) {
        this.subscribe();
      }
    });
    this.setupUpdateObservable();
  }

  private unsubscribe(): void {
    if (this.updateSubscriber) {
      this.updateSubscriber.unsubscribe();
    }
  }

  private subscribe(): void {
    if (this.updateSubscriber) {
      this.updateSubscriber.unsubscribe();
    }
    this.updateSubscriber = this.updateObservable$.subscribe(() => {
      const seek = this.getSeek();

      this.onDurationSubject.next(seek);
      this.onMaxDurationSubject.next(this.getMaxDuration());

      this.sound.volume(this.calculateVolume(seek));

      if (seek < 0) {
        this.seekTo(0);
      }

      if (this._prevSound && this._prevSound.isMixPointActive) {
        const start = seek - this._startSegment;
        const lengthFromMixPrev = this._prevSound.endSegment - this._prevSound.mixPoint;
        if (start > lengthFromMixPrev) {
          this._prevSound.stopAudio();
        } else {
          if (this.shouldSeekPrev) {
            this._prevSound.seekTo(this._prevSound.mixPoint + start);
            this.shouldSeekPrev = false;
          }
          if (!this._prevSound.isPlaying) {
            this._prevSound.seekTo(this._prevSound.mixPoint + start);
            this._prevSound.playMusic();
          }
        }
      }

      if (this._nextSound) {
        if (this._isMixPointActive && seek > this.mixPoint) {
          const fromStart = seek - this.mixPoint;
          if (this.shouldSeekNext) {
            this._nextSound.seekTo(this._nextSound.startSegment + fromStart);
            this.shouldSeekNext = false;
          }
          if (!this._nextSound.isPlaying) {
            this._nextSound.playMusic();
          }
        } else {
          if (this._nextSound.isPlaying) {
            this._nextSound.pauseMusic();
          }
        }
      }
    });
  }

  private calculateVolume(seek: number): number {
    let volume = 1;

    if (this._isIntroPointActive && seek < this._introPoint) {
      volume = (seek - this._startSegment) / (this._introPoint - this._startSegment);
    } else if (this._isOutroPointActive && this._outroPoint < seek) {
      volume = 1 - (seek - this._outroPoint) / (this._endSegment - this._outroPoint);
    }

    return volume > 0 ? volume : 0;
  }

  private setupUpdateObservable(): void {
    this.onDurationSubject.next(0);
    this.onMaxDurationSubject.next(0);
    this.updateObservable$ = interval(100);
  }

  getSeek(): number {
    // return 0;
    if (!this.sound || !this.isLoaded) {
      return 0;
    }

    const seek = Math.round(this.sound.seek() * 100) / 100;
    if (isNaN(seek)) {
      return this.lastSeek;
    }
    this.lastSeek = seek;

    return this.lastSeek;
  }

  getMaxDuration(): number {
    if (!this.sound || !this.isLoaded) {
      return 0;
    }

    return Math.round(this.sound.duration() * 100) / 100;
  }

  playFromStart() {
    this.seekTo(this._startSegment);
    this.playMusic();
  }

  playFromEnd() {
    const extroVal = this._isOutroPointActive
      ? this._outroPoint
      : Number.MAX_SAFE_INTEGER;
    const mixVal = this._isMixPointActive ? this._mixPoint : Number.MAX_SAFE_INTEGER;
    const starting = Math.min(extroVal, mixVal);
    if (starting === Number.MAX_SAFE_INTEGER) {
      return;
    }
    this.seekTo(starting);
    this.playMusic();
  }

  stopAudio() {
    this.sound.stop();
  }

  playMusic() {
    if (this.isPlaying || this.sound.playing()) {
      return;
    }
    this._isPlaying = true;
    const seek = this.getSeek();
    this.sound.volume(this.calculateVolume(seek));
    this.sound.play();
  }

  get isPlaying(): boolean {
    return this._isPlaying;
  }

  set startSegment(value: number) {
    if (value === this._startSegment) {
      return;
    }
    this._startSegment = value;
  }

  get startSegment(): number {
    return this._startSegment;
  }

  set endSegment(value: number) {
    if (value === this._endSegment) {
      return;
    }
    this._endSegment = value;
  }

  get endSegment(): number {
    return this._endSegment;
  }

  set introPoint(value: number) {
    if (value === this._introPoint) {
      return;
    }
    this._introPoint = value;
  }

  get introPoint(): number {
    return this._introPoint;
  }

  set isIntroPointActive(value: boolean) {
    if (value === this._isIntroPointActive) {
      return;
    }
    this._isIntroPointActive = value;
  }

  get isIntroPointActive(): boolean {
    return this._isIntroPointActive;
  }

  set outroPoint(value: number) {
    if (value === this._outroPoint) {
      return;
    }
    this._outroPoint = value;
  }

  get outroPoint(): number {
    return this._outroPoint;
  }

  get onEnd$(): Observable<void> {
    return this.onEndSubject.asObservable();
  }

  get onStop$(): Observable<void> {
    return this.onStopSubject.asObservable();
  }

  get onPlay$(): Observable<void> {
    return this.onPlaySubject.asObservable();
  }

  get onPause$(): Observable<void> {
    return this.onPauseSubject.asObservable();
  }

  get playing$(): Observable<boolean> {
    return this.playingSubject.asObservable();
  }

  get onDuration$(): Observable<number> {
    return this.onDurationSubject.asObservable().pipe(filter(data => !isNaN(data)));
  }

  get onMaxDuration$(): Observable<number> {
    return this.onMaxDurationSubject.asObservable().pipe(filter(data => !isNaN(data)));
  }

  set isOutroPointActive(value: boolean) {
    if (value === this._isOutroPointActive) {
      return;
    }
    this._isOutroPointActive = value;
  }

  get isOutroPointActive(): boolean {
    return this._isOutroPointActive;
  }

  set mixPoint(value: number) {
    if (value === this._mixPoint) {
      return;
    }
    this._mixPoint = value;
  }

  get mixPoint(): number {
    return this._mixPoint;
  }

  set isMixPointActive(value: boolean) {
    if (value === this._isMixPointActive) {
      return;
    }
    this._isMixPointActive = value;
  }

  get isMixPointActive(): boolean {
    return this._isMixPointActive;
  }

  set nextSound(value: Sound) {
    if (this._nextSound) {
      this._nextSound.stopAudio();
      this._nextSound.destroy();
    }
    this._nextSound = value;
  }

  get nextSound(): Sound {
    return this._nextSound;
  }

  set prevSound(value: Sound) {
    if (this._prevSound) {
      this._prevSound.stopAudio();
      this._prevSound.destroy();
    }
    this._prevSound = value;
  }

  get prevSound(): Sound {
    return this._prevSound;
  }

  public pauseMusic(): void {
    if (this.sound) {
      this.sound.pause();
    }
  }

  public seekTo(secs: number): void {
    this.shouldSeekPrev = true;
    this.shouldSeekNext = true;
    if (this.sound && isFinite(secs)) {
      this.sound.seek(secs);
      this.onDurationSubject.next(secs);
    }
    this.shouldSeekPrev = true;
    this.shouldSeekNext = true;
  }
}
